import type { ThemeConfig } from 'antd';
import { theme } from 'antd';

/**
 * Light is stock antd on purpose -- falcon carries no brand token overrides.
 * Dark is antd's own algorithm rather than a hand-built palette.
 *
 * `cssVar` is what makes the SCSS in this app theme-aware: antd emits every
 * token as a `--ant-*` custom property, so a rule written against
 * `var(--ant-color-bg-container)` follows the active algorithm without a
 * parallel `.dark-mode` copy of itself. `hashed: false` keeps the emitted
 * class names stable, which the var output relies on.
 */
/**
 * antd's dark `colorPrimary` is #1668dc, which measures 3.55:1 against the dark
 * container -- under the 4.5:1 AA floor for text, so links read as muddy. This
 * is the next step up antd's own dark palette (its `colorPrimaryHover`), which
 * measures 5.21:1. Only `colorLink` is set; antd derives hover and active from
 * it. Light mode needs no such nudge: #1677ff on white is already 4.10:1 and
 * antd's default link treatment is unchanged there.
 */
const DARK_LINK = '#3c89e8';

export const getThemeConfig = (darkMode: boolean): ThemeConfig => ({
  algorithm: darkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
  // antd's default `ant` prefix rather than an app-specific one, so shared
  // components in @owl-frontend/components can be written against the same
  // variables without naming falcon.
  cssVar: { prefix: 'ant' },
  hashed: false,
  ...(darkMode && { token: { colorLink: DARK_LINK } }),
});

export const DARK_MODE_STORAGE_KEY = 'falcon.darkMode';

/**
 * Mirrors the pre-paint probe in config/webpack/html.ts, which runs before the
 * bundle loads and cannot import this. Keep the two in sync.
 */
export function readDarkMode(): boolean {
  try {
    return window.localStorage.getItem(DARK_MODE_STORAGE_KEY) === 'true';
  } catch {
    return false;
  }
}

export function writeDarkMode(darkMode: boolean): void {
  try {
    window.localStorage.setItem(DARK_MODE_STORAGE_KEY, String(darkMode));
  } catch {} // eslint-disable-line no-empty
}
